import {Trans} from '@lingui/macro';
import React, {FC, ReactNode, useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {collectEnumValues} from '../../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {isValidCompanyName} from '../../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {commonProviderDomains, extractDomainFromEmail} from '../../../../helpers/domainHelpers';
import {CompanyModel} from '../../../../models/api/companyModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {ApolloEnrichmentData, IndustriesEnum} from '../../../../models/api/userMetadataModel';
import {
  CarouselNavigationButtonVariantsEnum,
  CarouselSlideButtonsContainer,
  CarouselSlideContentRight,
  CarouselSlideFooterRight,
} from '../../carouselCommon';
import {CarouselNavigationButton} from '../../carouselNavigationButton';
import {
  IndustriesDropdownItems,
  WelcomeCarouselDropdownError,
  WelcomeCarouselDropdownPlaceholder,
} from '../welcomeCarouselCommon';
import {WelcomeCarouselDropdown} from './welcomeCarouselDropdown';
import {
  computeInitialDropdownSelection,
  findFrontIndustryFromApolloIndustry,
} from './welcomeCarouselFormHelpers';

/*
 * Constants.
 */

enum CompanyFormValueTypesEnum {
  COMPANY_NAME = 'COMPANY_NAME',
  INDUSTRY = 'INDUSTRY',
  COMPANY_SIZE = 'COMPANY_SIZE',
  TEAM_SIZE = 'TEAM_SIZE',
}

interface SizeRangeDropdownItem {
  title: ReactNode;
  key: number;
  range: [number, number];
}

const companySizeDropdownItems: SizeRangeDropdownItem[] = [
  {title: <Trans id="number-of-employees-1">1-5 employees</Trans>, key: 1, range: [1, 5]},
  {title: <Trans id="number-of-employees-6">6-10 employees</Trans>, key: 6, range: [6, 10]},
  {title: <Trans id="number-of-employees-11">11-25 employees</Trans>, key: 11, range: [11, 25]},
  {title: <Trans id="number-of-employees-26">26-50 employees</Trans>, key: 26, range: [26, 50]},
  {title: <Trans id="number-of-employees-51">51-100 employees</Trans>, key: 51, range: [51, 100]},
  {title: <Trans id="number-of-employees-101">101-499 employees</Trans>, key: 101, range: [101, 499]},
  {title: <Trans id="number-of-employees-501">500+ employees</Trans>, key: 501, range: [500, Infinity]},
];

const teamSizeDropdownItems: SizeRangeDropdownItem[] = [
  {title: <Trans id="team-size-1">1-5 users</Trans>, key: 1, range: [1, 5]},
  {title: <Trans id="team-size-6">6-10 users</Trans>, key: 6, range: [6, 10]},
  {title: <Trans id="team-size-11">11-25 users</Trans>, key: 11, range: [11, 25]},
  {title: <Trans id="team-size-26">26-50 users</Trans>, key: 26, range: [26, 50]},
  {title: <Trans id="team-size-51">51-100 users</Trans>, key: 51, range: [51, 100]},
  {title: <Trans id="team-size-101">101+ users</Trans>, key: 101, range: [101, Infinity]},
];

// Company names we should ignore when initializing the company form name.
const placeholderCompanyNames = ['Company', '[Unknown Company Name]'];

/*
 * Props.
 */

interface WelcomeCarouselCompanyFormProps {
  user: TeammateModel;
  company: CompanyModel;
  apolloEnrichmentData: ApolloEnrichmentData;
  /** User's metadata team size. By default, it's 1. */
  teamSize?: number;
  /** An optional industry. We enforce IndustriesEnum in our component, but not at the model level. */
  industry?: string;
  /** Handler to go to previous slide in the carousel. */
  onPreviousClick: Handler;
  /** Handler for completing the company form. */
  submitForm: HandlerOf<CompanyFormOptions>;
}

export interface CompanyFormOptions {
  name: string;
  companySize: number;
  teamSize: number;
  industry?: string;
}

/*
 * Style.
 */

const StyledFormDiv = styled.div`
  display: grid;
  grid-template-rows: repeat(4, 1fr);
  grid-row-gap: 10px;
`;

/*
 * Component.
 */

// Map of value types to their respective validators.
const validatorsForValueType = {
  [CompanyFormValueTypesEnum.COMPANY_NAME]: isValidCompanyName,
  // Use `any` so that we can map through CompanyFormValueTypesEnum during validation.
  [CompanyFormValueTypesEnum.INDUSTRY]: (i: any) => Boolean(i),
  [CompanyFormValueTypesEnum.COMPANY_SIZE]: (s: number | undefined) => Boolean(s),
  [CompanyFormValueTypesEnum.TEAM_SIZE]: (s: number | undefined) => Boolean(s),
};

export const WelcomeCarouselCompanyForm: FC<WelcomeCarouselCompanyFormProps> = (props) => {
  const {user, company, onPreviousClick, submitForm, apolloEnrichmentData} = props;

  // Company form values.
  const [companyName, setCompanyName] = useState(
    computeInitialCompanyName(user.email, company.name, apolloEnrichmentData.companyName),
  );

  const initialIndustryResult = computeInitialIndustry(props.industry, apolloEnrichmentData.industry);
  // Show the industry field if we didn't enrich it.
  const shouldShowIndustryDropdown = !initialIndustryResult.isUsingEnrichedValue;
  const [industry, setIndustry] = useState<IndustriesEnum | undefined>(
    computeInitialDropdownSelection(IndustriesEnum, initialIndustryResult.value),
  );

  const initialCompanySizeResult = computeInitialCompanySize(
    company.preferences.companySize,
    apolloEnrichmentData.numberOfEmployees,
  );
  // Snow the company size field if we didn't enrich it.
  const shouldShowCompanySizeField = !initialCompanySizeResult.isUsingEnrichedValue;
  const [companySize, setCompanySize] = useState<number | undefined>(initialCompanySizeResult.value);

  const [teamSize, setTeamSize] = useState<number | undefined>(computeInitialTeamSize(props.teamSize));

  // Form errors.
  const [formErrors, setFormErrors] = useState<Array<CompanyFormValueTypesEnum>>([]);

  // The value most recently modified that we need to validate.
  const [valueToValidate, setValueToValidate] = useState<CompanyFormValueTypesEnum | undefined>();

  // Prevents inputs in company form from being in error states on the initial load.
  const isInitialCompanyFormRenderRef = useRef(false);

  // Map of value types to their respective state variables.
  const stateForValueType = useMemo(
    () => ({
      [CompanyFormValueTypesEnum.COMPANY_NAME]: companyName,
      [CompanyFormValueTypesEnum.INDUSTRY]: industry,
      [CompanyFormValueTypesEnum.COMPANY_SIZE]: companySize,
      [CompanyFormValueTypesEnum.TEAM_SIZE]: teamSize,
    }),
    [companyName, industry, companySize, teamSize],
  );

  useEffect(() => {
    // On the initial render of the company form, do not validate.
    if (isInitialCompanyFormRenderRef.current) {
      isInitialCompanyFormRenderRef.current = false;
    }

    // If there is no value to validate, skip.
    if (!valueToValidate) {
      return;
    }

    const state = stateForValueType[valueToValidate];
    const validator = validatorsForValueType[valueToValidate];
    const isValueValid = validator(state);
    const hasFormErrorForValue = formErrors.includes(valueToValidate);

    // If the value is valid, but an error exists, remove it.
    if (isValueValid && hasFormErrorForValue) {
      setFormErrors(formErrors.filter((e) => e !== valueToValidate));
    }

    // If the value is invalid, but an error does not exist, add it.
    if (!isValueValid && !hasFormErrorForValue) {
      setFormErrors(formErrors.concat(valueToValidate));
    }
  }, [formErrors, companyName, industry, companySize, stateForValueType, valueToValidate]);

  const onSubmitForm = () => {
    let newFormErrors = formErrors.slice();
    collectEnumValues(CompanyFormValueTypesEnum).forEach((valueType) => {
      const state = stateForValueType[valueType];
      const validator = validatorsForValueType[valueType];
      const isValueValid = validator(state);
      const hasFormErrorForValue = formErrors.includes(valueType);

      // If the value is valid, but an error exists, remove it.
      if (isValueValid && hasFormErrorForValue) {
        newFormErrors = newFormErrors.filter((e) => e !== valueType);
      }

      // If the value is invalid, but an error does not exist, add it.
      if (!isValueValid && !hasFormErrorForValue) {
        newFormErrors = newFormErrors.concat(valueType);
      }
    });

    if (newFormErrors.length > 0) {
      setFormErrors(newFormErrors);
      return;
    }

    submitForm({
      name: companyName,
      companySize: companySize ?? 1,
      teamSize: teamSize ?? 1,
      industry,
    });
  };

  const renderCompanyFormContent = () => (
    <>
      <TextBox
        value={companyName}
        onChange={(value) => {
          setValueToValidate(CompanyFormValueTypesEnum.COMPANY_NAME);
          setCompanyName(value);
        }}
        hasError={formErrors.includes(CompanyFormValueTypesEnum.COMPANY_NAME)}
        size={VisualSizesEnum.MEDIUM}
        placeholder="Enter company name"
        label={<Trans id="welcomecarousel-companyform-companyname-label">Company name</Trans>}
      />
      {shouldShowIndustryDropdown && (
        <div>
          <WelcomeCarouselDropdown<IndustriesEnum>
            placeholder={
              <WelcomeCarouselDropdownPlaceholder>
                <Trans id="welcomecarousel-companyform-industry-placeholder">Select industry</Trans>
              </WelcomeCarouselDropdownPlaceholder>
            }
            selectedItemKey={industry}
            items={IndustriesDropdownItems}
            onSelect={(d) => {
              setValueToValidate(CompanyFormValueTypesEnum.INDUSTRY);
              setIndustry(d.key);
            }}
            renderDescription={() => <Trans id="welcomecarousel-companyform-industry-label">Industry</Trans>}
          />
          <WelcomeCarouselDropdownError>
            {maybeRenderDropdownErrorText(formErrors, CompanyFormValueTypesEnum.INDUSTRY)}
          </WelcomeCarouselDropdownError>
        </div>
      )}
      {shouldShowCompanySizeField && (
        <div>
          <WelcomeCarouselDropdown<number>
            placeholder={
              <WelcomeCarouselDropdownPlaceholder>
                <Trans id="welcomecarousel-companyform-companysize-placeholder">
                  Select number of employees
                </Trans>
              </WelcomeCarouselDropdownPlaceholder>
            }
            selectedItemKey={companySize}
            items={companySizeDropdownItems}
            onSelect={(d) => {
              setValueToValidate(CompanyFormValueTypesEnum.COMPANY_SIZE);
              setCompanySize(d.key);
            }}
            renderDescription={() => (
              <Trans id="welcomecarousel-companyform-companysize-label">Number of employees</Trans>
            )}
          />
          <WelcomeCarouselDropdownError>
            {maybeRenderDropdownErrorText(formErrors, CompanyFormValueTypesEnum.COMPANY_SIZE)}
          </WelcomeCarouselDropdownError>
        </div>
      )}
      <div>
        <WelcomeCarouselDropdown<number>
          placeholder={
            <WelcomeCarouselDropdownPlaceholder>
              <Trans id="welcomecarousel-companyform-teamsize-placeholder">
                Select number of Front users
              </Trans>
            </WelcomeCarouselDropdownPlaceholder>
          }
          selectedItemKey={teamSize}
          items={teamSizeDropdownItems}
          onSelect={(d) => {
            setValueToValidate(CompanyFormValueTypesEnum.TEAM_SIZE);
            setTeamSize(d.key);
          }}
          renderDescription={() => (
            <Trans id="welcomecarousel-companyform-teamsize-label">Expected number of Front users</Trans>
          )}
        />
        <WelcomeCarouselDropdownError>
          {maybeRenderDropdownErrorText(formErrors, CompanyFormValueTypesEnum.TEAM_SIZE)}
        </WelcomeCarouselDropdownError>
      </div>
    </>
  );

  return (
    <>
      <CarouselSlideContentRight>
        <StyledFormDiv>{renderCompanyFormContent()}</StyledFormDiv>
      </CarouselSlideContentRight>
      <CarouselSlideFooterRight>
        <CarouselSlideButtonsContainer>
          <CarouselNavigationButton
            variant={CarouselNavigationButtonVariantsEnum.SUBDUED_LINK}
            onClick={onPreviousClick}
          >
            <Trans id="welcomecarousel-companyform-previous">Previous</Trans>
          </CarouselNavigationButton>
          <CarouselNavigationButton
            variant={CarouselNavigationButtonVariantsEnum.PRIMARY}
            onClick={onSubmitForm}
          >
            <Trans id="welcomecarousel-companyform-next">Next</Trans>
          </CarouselNavigationButton>
        </CarouselSlideButtonsContainer>
      </CarouselSlideFooterRight>
    </>
  );
};

/*
 * Helpers.
 */

function maybeRenderDropdownErrorText(
  formErrors: Array<CompanyFormValueTypesEnum>,
  dropdownType: CompanyFormValueTypesEnum,
) {
  if (!formErrors.includes(dropdownType)) {
    return null;
  }

  switch (dropdownType) {
    case CompanyFormValueTypesEnum.INDUSTRY:
      return <Trans id="welcomecarousel-companyform-industry-error">Please select an industry</Trans>;

    case CompanyFormValueTypesEnum.TEAM_SIZE:
      return (
        <Trans id="welcomecarousel-companyform-teamsize-error">Please select the number of Front users</Trans>
      );

    case CompanyFormValueTypesEnum.COMPANY_SIZE:
      return <Trans id="welcomecarousel-companyform-companysize-error">Please select a company size</Trans>;

    default:
      return null;
  }
}

function computeInitialCompanyName(userEmail: string, currentName: string, enrichedName: string | undefined) {
  // If the current name isn't one of the placeholder names, return it.
  if (currentName && !placeholderCompanyNames.includes(currentName)) {
    return currentName;
  }

  // Otherwise, there's no name yet, so try the enriched name before trying to
  // extract one from the domain.
  if (enrichedName) {
    return enrichedName;
  }

  // Otherwise, let's try to prepopulate using the domain of the user's email.
  const domain = extractDomainFromEmail(userEmail);
  if (!domain || commonProviderDomains.includes(domain)) {
    return '';
  }

  // meetingbird.com -> meetingbird, frontapp.random.com -> frontapp
  return domain.replace(/\..*/, '');
}

function computeInitialIndustry(
  industryValue: string | undefined,
  enrichedIndustryValue: string | undefined,
) {
  // If there's a matching enriched industry value, use that first.
  const matchingEnrichedIndustryValue = findFrontIndustryFromApolloIndustry(enrichedIndustryValue);
  if (matchingEnrichedIndustryValue) {
    return {value: matchingEnrichedIndustryValue, isUsingEnrichedValue: true};
  }

  return {value: industryValue, isUsingEnrichedValue: false};
}

function computeInitialCompanySize(companySize: number | undefined, enrichedCompanySize: number | undefined) {
  // If there's a matching enriched company size, use that first.
  const matchingEnrichedCompanySize = findMatchingDropdownItem(companySizeDropdownItems, enrichedCompanySize);
  if (matchingEnrichedCompanySize) {
    return {value: matchingEnrichedCompanySize, isUsingEnrichedValue: true};
  }

  // Clear the field if it's the default value. Handles the case where the user
  // returns to this form and we can't distinguish between the default value and
  // a user-chosen value. Simplest to default to clearing the field.
  if (!companySize) {
    return {value: undefined, isUsingEnrichedValue: false};
  }

  // The user has explicitly chosen a company size that's not 1, so
  // try to use that size.
  const matchingCompanySize = findMatchingDropdownItem(companySizeDropdownItems, companySize);

  return {value: matchingCompanySize, isUsingEnrichedValue: false};
}

function computeInitialTeamSize(teamSize: number | undefined) {
  // Clear the field if it's the default value. Handles the case where the user
  // returns to this form and we can't distinguish between the default value and
  // a user-chosen value. Simplest to default to clearing the field.
  if (!teamSize) {
    return undefined;
  }

  return findMatchingDropdownItem(teamSizeDropdownItems, teamSize);
}

/**
 * Given a list of options representing ranges of numbers and a number, tries to
 * find the option whose range contains the given number.
 */
function findMatchingDropdownItem(options: SizeRangeDropdownItem[], size: number | undefined) {
  if (!size) {
    return undefined;
  }

  const matchingDropdownItem = options.find((item) => {
    const [low, high] = item.range;

    return size >= low && size <= high;
  });

  if (!matchingDropdownItem) {
    return undefined;
  }

  return matchingDropdownItem.key;
}
